import { useFilePlates, modelsApi } from '../api';
import {
  File,
  Layers,
  Package,
  AlertCircle,
  Clock,
  Box,
  Maximize,
  Move,
  RotateCw,
  Scale as ScaleIcon,
  Info,
  Image as ImageIcon,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { ModelFile, BuildPlateWithObjects, PlateObject } from '@maker-toolchest/shared';
import { ImageGallery } from '@/components/ImageGallery';
import { MetadataField } from '@/components/MetadataField';
import { SectionHeader } from '@/components/SectionHeader';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  formatFileSize,
  formatVolume,
  formatNumber,
  formatPlateType,
  formatPrintTime,
  formatDate,
} from '@/utils/formatting';

interface FileDetailViewProps {
  modelId: string;
  file: ModelFile;
}

export function FileDetailView({ modelId, file }: FileDetailViewProps) {
  const { data: platesResponse, isLoading } = useFilePlates(
    modelId,
    file.id,
    file.metadataStatus === 'COMPLETED'
  );

  // Fetch signed image URLs
  const { data: imageUrls } = useQuery({
    queryKey: ['models', modelId, 'files', file.id, 'image-urls'],
    queryFn: () => modelsApi.getFileImageUrls(modelId, file.id),
    enabled: file.metadataStatus === 'COMPLETED' && file.fileType === '3mf',
    staleTime: 30 * 60 * 1000, // Consider fresh for 30 minutes (URLs expire in 1 hour)
  });

  const plates = platesResponse?.plates || [];

  return (
    <div className="space-y-6">
      {/* File Info Section */}
      <section className="bg-background-subtle rounded-lg border border-border p-6">
        <SectionHeader icon={File} title="File Information" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MetadataField label="Filename" value={file.fileName} />
          <MetadataField label="File Type" value={<span className="uppercase">{file.fileType || 'STL'}</span>} />
          <MetadataField label="File Size" value={formatFileSize(file.fileSizeBytes)} />
          <MetadataField label="Uploaded" value={formatDate(file.createdAt)} />
          <MetadataField label="Metadata Status" value={<MetadataStatusBadge status={file.metadataStatus} />} />
          <MetadataField label="Metadata Extracted" value={file.metadataExtractedAt ? formatDate(file.metadataExtractedAt) : null} />
        </div>
        {file.notes && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="text-xs font-medium text-foreground-muted uppercase mb-1">
              Notes
            </div>
            <div className="text-sm text-foreground whitespace-pre-wrap">{file.notes}</div>
          </div>
        )}
        {file.metadataError && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-start gap-2 text-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-medium uppercase mb-1">Extraction Error</div>
                <div className="text-sm">{file.metadataError}</div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Print Information Section - Comprehensive Metadata */}
      {file.metadataStatus === 'COMPLETED' && platesResponse?.metadata && (
        <section className="bg-background-subtle rounded-lg border border-border p-6">
          <SectionHeader icon={Info} title="Print Information" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MetadataField
              label="Slicer"
              value={
                (platesResponse.metadata.slicerName || platesResponse.metadata.slicerVersion) ? (
                  <>
                    {platesResponse.metadata.slicerName || 'Unknown'}
                    {platesResponse.metadata.slicerVersion && (
                      <span className="text-foreground-muted"> v{platesResponse.metadata.slicerVersion}</span>
                    )}
                  </>
                ) : null
              }
            />

            <MetadataField
              label="Nozzle"
              value={
                (platesResponse.metadata.nozzleDiameter || platesResponse.metadata.nozzleType) ? (
                  <>
                    {platesResponse.metadata.nozzleDiameter && <span>{platesResponse.metadata.nozzleDiameter}mm</span>}
                    {platesResponse.metadata.nozzleDiameter && platesResponse.metadata.nozzleType && ' • '}
                    {platesResponse.metadata.nozzleType && (
                      <span className="text-foreground-muted capitalize">
                        {platesResponse.metadata.nozzleType.replace(/_/g, ' ')}
                      </span>
                    )}
                  </>
                ) : null
              }
            />

            <MetadataField
              label="Estimated Print Time"
              icon={Clock}
              value={platesResponse.metadata.totalPrintTime != null ? formatPrintTime(platesResponse.metadata.totalPrintTime) : null}
            />

            <MetadataField
              label="Estimated Weight"
              value={platesResponse.metadata.totalWeight != null ? `${platesResponse.metadata.totalWeight.toFixed(1)}g` : null}
            />

            <MetadataField
              label="Filament Used"
              value={platesResponse.metadata.totalFilamentUsed != null ? `${platesResponse.metadata.totalFilamentUsed.toFixed(1)}g` : null}
            />

            <MetadataField
              label="Filament Types"
              value={platesResponse.metadata.filamentTypes?.length > 0 ? platesResponse.metadata.filamentTypes.join(', ') : null}
            />

            <MetadataField
              label="Filament Vendors"
              value={platesResponse.metadata.filamentVendors?.length > 0 ? platesResponse.metadata.filamentVendors.join(', ') : null}
            />

            {/* Filament Colors */}
            {platesResponse.metadata.filamentColors && platesResponse.metadata.filamentColors.length > 0 && (
              <div className="md:col-span-2 lg:col-span-3">
                <div className="text-xs font-medium text-foreground-muted uppercase mb-2">
                  Filament Colors ({platesResponse.metadata.filamentColors.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {platesResponse.metadata.filamentColors.map((color, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded border-2 border-border shadow-sm"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                      <span className="text-xs text-foreground-muted font-mono">{color}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Requirements Badges */}
            {(platesResponse.metadata.requiresSupport != null || platesResponse.metadata.requiresMultiMaterial != null) && (
              <div className="md:col-span-2 lg:col-span-3">
                <div className="text-xs font-medium text-foreground-muted uppercase mb-2">
                  Requirements
                </div>
                <div className="flex flex-wrap gap-2">
                  {platesResponse.metadata.requiresSupport && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                      Requires Support
                    </span>
                  )}
                  {platesResponse.metadata.requiresMultiMaterial && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info/10 text-info border border-info/20">
                      Multi-Material
                    </span>
                  )}
                  {!platesResponse.metadata.requiresSupport && !platesResponse.metadata.requiresMultiMaterial && (
                    <span className="text-sm text-foreground-muted">None</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* File-Level Thumbnails Section */}
      {file.metadataStatus === 'COMPLETED' && file.fileType === '3mf' && imageUrls?.thumbnails && (
        imageUrls.thumbnails.thumbnail3mf ||
        imageUrls.thumbnails.thumbnailMiddle ||
        imageUrls.thumbnails.thumbnailSmall
      ) && (
        <section className="bg-background-subtle rounded-lg border border-border p-6">
          <SectionHeader icon={ImageIcon} title="File Thumbnails" />
          <ImageGallery
            images={[
              imageUrls.thumbnails.thumbnail3mf && {
                url: imageUrls.thumbnails.thumbnail3mf,
                label: '3MF Thumbnail',
                alt: 'Main 3MF thumbnail',
              },
              imageUrls.thumbnails.thumbnailMiddle && {
                url: imageUrls.thumbnails.thumbnailMiddle,
                label: 'Medium Thumbnail',
                alt: 'Medium size thumbnail',
              },
              imageUrls.thumbnails.thumbnailSmall && {
                url: imageUrls.thumbnails.thumbnailSmall,
                label: 'Small Thumbnail',
                alt: 'Small size thumbnail',
              },
            ].filter(Boolean) as Array<{ url: string; label?: string; alt?: string }>}
          />
        </section>
      )}

      {/* Metadata Sections */}
      {file.metadataStatus === 'COMPLETED' && plates && plates.length > 0 && (
        <>
          {/* Build Plates with Objects */}
          <section className="bg-background-subtle rounded-lg border border-border p-6">
            <SectionHeader icon={Layers} title={`Build Plates (${plates.length})`} />
            <div className="space-y-4">
              {plates.map((plate) => {
                const plateImages = imageUrls?.plates?.find(p => p.plateNumber === plate.plateNumber);
                return (
                  <BuildPlateCard key={plate.id} plate={plate} images={plateImages} />
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-foreground-muted">
            <Clock className="h-5 w-5 animate-spin" />
            <span>Loading metadata...</span>
          </div>
        </div>
      )}

      {/* Pending/Processing State */}
      {(file.metadataStatus === 'PENDING' || file.metadataStatus === 'PROCESSING') && (
        <section className="bg-background-subtle rounded-lg border border-border p-12">
          <div className="flex flex-col items-center justify-center text-center space-y-3">
            <Clock className="h-12 w-12 text-primary animate-pulse" />
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {file.metadataStatus === 'PENDING'
                  ? 'Metadata Extraction Queued'
                  : 'Extracting Metadata...'}
              </h3>
              <p className="text-sm text-foreground-muted">
                {file.metadataStatus === 'PENDING'
                  ? 'File metadata extraction is queued and will begin shortly.'
                  : 'Please wait while we extract detailed information from your file.'}
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function MetadataStatusBadge({ status }: { status: string }) {
  const config = {
    PENDING: { color: 'text-yellow-600 bg-yellow-100', text: 'Queued' },
    PROCESSING: { color: 'text-blue-600 bg-blue-100', text: 'Processing' },
    COMPLETED: { color: 'text-green-600 bg-green-100', text: 'Completed' },
    FAILED: { color: 'text-red-600 bg-red-100', text: 'Failed' },
  };

  const { color, text } = config[status as keyof typeof config] || config.PENDING;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${color}`}>
      {text}
    </span>
  );
}

interface PlateImages {
  plateNumber: number;
  thumbnail: string | null;
  topView: string | null;
  pick: string | null;
}

function BuildPlateCard({ plate, images }: { plate: BuildPlateWithObjects; images?: PlateImages }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasObjects = plate.objects && plate.objects.length > 0;
  const hasBoundingBox =
    plate.boundingMinX != null &&
    plate.boundingMinY != null &&
    plate.boundingMinZ != null &&
    plate.boundingMaxX != null &&
    plate.boundingMaxY != null &&
    plate.boundingMaxZ != null;
  const hasImages = images && (images.thumbnail || images.topView || images.pick);
  const hasAdditionalDetails = hasBoundingBox || plate.firstExtruder != null || (plate.filamentSequences && plate.filamentSequences.length > 0) || hasObjects;

  return (
    <div className="bg-background rounded-lg border border-border p-4">
      {/* Always Visible Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground">
              {plate.name || `Plate ${plate.plateNumber}`}
            </h3>
            {hasObjects && (
              <span className="text-xs text-foreground-muted">
                ({plate.objects.length} object{plate.objects.length !== 1 ? 's' : ''})
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1 mt-1">
            {plate.bedType && (
              <div className="text-xs">
                <span className="text-foreground-muted">Plate Type: </span>
                <span className="text-foreground">{formatPlateType(plate.bedType)}</span>
              </div>
            )}
            {plate.filamentSequences && plate.filamentSequences.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-medium text-foreground-muted mb-1.5">
                  Colors ({plate.filamentSequences.filter(seq => seq.color).length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {plate.filamentSequences.map((seq) => (
                    seq.color && (
                      <div key={seq.id} className="flex items-center gap-1.5">
                        <div
                          className="w-7 h-7 rounded border-2 border-border shadow-sm"
                          style={{ backgroundColor: seq.color }}
                          title={seq.color}
                        />
                        <span className="text-xs text-foreground-muted font-mono">{seq.color}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {hasAdditionalDetails && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-background-subtle"
          >
            {isExpanded ? (
              <>
                <ChevronDown className="h-4 w-4" />
                <span>Hide Details</span>
              </>
            ) : (
              <>
                <ChevronRight className="h-4 w-4" />
                <span>Show Details</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Always Visible: Plate Images */}
      {hasImages && (
        <div className="mt-3">
          <ImageGallery
            images={[
              images.thumbnail && {
                url: images.thumbnail,
                label: 'Thumbnail',
                alt: `Plate ${plate.plateNumber} thumbnail view`,
              },
              images.topView && {
                url: images.topView,
                label: 'Top View',
                alt: `Plate ${plate.plateNumber} top view`,
              },
              images.pick && {
                url: images.pick,
                label: 'Pick View',
                alt: `Plate ${plate.plateNumber} pick view`,
              },
            ].filter(Boolean) as Array<{ url: string; label?: string; alt?: string }>}
          />
        </div>
      )}

      {/* Collapsible Details */}
      {isExpanded && hasAdditionalDetails && (
        <div className="mt-4 pt-4 border-t border-border space-y-4">
          {/* Plate Metadata */}
          {(hasBoundingBox || plate.firstExtruder != null) && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              {hasBoundingBox && (
                <div>
                  <div className="font-medium text-foreground-muted mb-1">Dimensions (mm)</div>
                  <div className="text-foreground">
                    {Math.round((plate.boundingMaxX ?? 0) - (plate.boundingMinX ?? 0))} ×{' '}
                    {Math.round((plate.boundingMaxY ?? 0) - (plate.boundingMinY ?? 0))} ×{' '}
                    {Math.round((plate.boundingMaxZ ?? 0) - (plate.boundingMinZ ?? 0))}
                  </div>
                </div>
              )}
              {plate.firstExtruder != null && (
                <div>
                  <div className="font-medium text-foreground-muted mb-1">First Extruder</div>
                  <div className="text-foreground">{plate.firstExtruder}</div>
                </div>
              )}
            </div>
          )}

          {/* Filament Sequence */}
          {plate.filamentSequences && plate.filamentSequences.length > 0 && (
            <div>
              <div className="text-xs font-medium text-foreground-muted mb-2">
                Filament Sequence
              </div>
              <div className="flex flex-wrap gap-2">
                {plate.filamentSequences.map((seq, i) => (
                  <div
                    key={seq.id}
                    className="flex items-center gap-2 px-2 py-1 rounded bg-background-subtle"
                  >
                    <span className="text-xs text-foreground-muted">#{i + 1}</span>
                    {seq.color && (
                      <div
                        className="w-4 h-4 rounded border border-border"
                        style={{ backgroundColor: seq.color }}
                        title={seq.color}
                      />
                    )}
                    {seq.filamentId && (
                      <span className="text-xs text-foreground">ID: {seq.filamentId}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Objects */}
          {hasObjects && (
            <div>
              <div className="text-xs font-medium text-foreground-muted mb-3 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Objects on this Plate
              </div>
              <div className="space-y-3">
                {plate.objects.map((obj) => (
                  <ObjectCard key={obj.id} object={obj} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ObjectCard({ object }: { object: PlateObject }) {
  const hasBoundingBox =
    object.boundingMinX != null &&
    object.boundingMinY != null &&
    object.boundingMinZ != null &&
    object.boundingMaxX != null &&
    object.boundingMaxY != null &&
    object.boundingMaxZ != null;

  return (
    <div className="bg-background rounded-lg border border-border p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-primary flex-shrink-0" />
          <h4 className="font-medium text-foreground">
            {object.name || `Object ${object.objectId || ''}`}
          </h4>
          {object.extruder && (
            <span className="text-xs text-foreground-muted">
              (Extruder {object.extruder})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {object.filamentColor && (
            <div className="flex items-center gap-1">
              <div
                className="w-6 h-6 rounded border-2 border-border shadow-sm flex-shrink-0"
                style={{ backgroundColor: object.filamentColor }}
                title={`Filament: ${object.filamentColor}`}
              />
            </div>
          )}
          {object.colorHex && object.colorHex !== object.filamentColor && (
            <div
              className="w-6 h-6 rounded border border-border/50 flex-shrink-0 opacity-60"
              style={{ backgroundColor: object.colorHex }}
              title={`Model Color: ${object.colorHex}`}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
        {/* Position */}
        {(object.positionX != null || object.positionY != null || object.positionZ != null) && (
          <div>
            <div className="font-medium text-foreground-muted mb-1 flex items-center gap-1">
              <Move className="h-3 w-3" />
              Position (mm)
            </div>
            <div className="text-foreground font-mono">
              X: {object.positionX?.toFixed(2) || 0}
              <br />
              Y: {object.positionY?.toFixed(2) || 0}
              <br />
              Z: {object.positionZ?.toFixed(2) || 0}
            </div>
          </div>
        )}

        {/* Rotation */}
        {(object.rotationX != null || object.rotationY != null || object.rotationZ != null) && (
          <div>
            <div className="font-medium text-foreground-muted mb-1 flex items-center gap-1">
              <RotateCw className="h-3 w-3" />
              Rotation (rad)
            </div>
            <div className="text-foreground font-mono">
              X: {object.rotationX?.toFixed(3) || 0}
              <br />
              Y: {object.rotationY?.toFixed(3) || 0}
              <br />
              Z: {object.rotationZ?.toFixed(3) || 0}
            </div>
          </div>
        )}

        {/* Scale */}
        {(object.scaleX != null || object.scaleY != null || object.scaleZ != null) && (
          <div>
            <div className="font-medium text-foreground-muted mb-1 flex items-center gap-1">
              <ScaleIcon className="h-3 w-3" />
              Scale
            </div>
            <div className="text-foreground font-mono">
              X: {object.scaleX?.toFixed(2) || 1}
              <br />
              Y: {object.scaleY?.toFixed(2) || 1}
              <br />
              Z: {object.scaleZ?.toFixed(2) || 1}
            </div>
          </div>
        )}

        {/* Bounding Box */}
        {hasBoundingBox && (
          <div>
            <div className="font-medium text-foreground-muted mb-1 flex items-center gap-1">
              <Box className="h-3 w-3" />
              Dimensions (mm)
            </div>
            <div className="text-foreground">
              {Math.round((object.boundingMaxX ?? 0) - (object.boundingMinX ?? 0))} ×{' '}
              {Math.round((object.boundingMaxY ?? 0) - (object.boundingMinY ?? 0))} ×{' '}
              {Math.round((object.boundingMaxZ ?? 0) - (object.boundingMinZ ?? 0))}
            </div>
          </div>
        )}

        {/* Volume */}
        {object.volumeMm3 && (
          <div>
            <div className="font-medium text-foreground-muted mb-1 flex items-center gap-1">
              <Maximize className="h-3 w-3" />
              Volume
            </div>
            <div className="text-foreground">{formatVolume(object.volumeMm3)}</div>
          </div>
        )}

        {/* Triangle Count */}
        {object.triangleCount && (
          <div>
            <div className="font-medium text-foreground-muted mb-1 flex items-center gap-1">
              <Info className="h-3 w-3" />
              Triangles
            </div>
            <div className="text-foreground">{formatNumber(object.triangleCount)}</div>
          </div>
        )}
      </div>

      {/* Additional metadata */}
      {(object.layerHeight || object.printArea || object.pid) && (
        <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          {object.layerHeight && (
            <div>
              <div className="font-medium text-foreground-muted mb-1">Layer Height</div>
              <div className="text-foreground">{object.layerHeight} mm</div>
            </div>
          )}
          {object.printArea && (
            <div>
              <div className="font-medium text-foreground-muted mb-1">Print Area</div>
              <div className="text-foreground">{object.printArea} mm²</div>
            </div>
          )}
          {object.pid && (
            <div>
              <div className="font-medium text-foreground-muted mb-1">Material PID</div>
              <div className="text-foreground font-mono">{object.pid}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
